import { create } from 'zustand';
import { ManufacturerEpdDataService } from '@/nodes/product-epd-data-modal/api/ManufacturerEpdDataService';
import type { SelectOption } from '@shared/components/CustomSelect/CustomSelect';
import { ManufacturerEpdData } from '@/shared/models/manufacturer-epd-data';
import { ProductEpdDataState } from '@/shared/models/product-epd-data-state';
import { SelectedEpdData } from '@/shared/models/selected-epd-data';
import type { ProductEpdDataModalTranslations } from '@shared/components/ProductEpdDataModal/ProductEpdDataModal';
import type { RequestMissingEpdModalTranslations } from '@shared/components/RequestMissingEpdModal/RequestMissingEpdModal';

class ProductEpdDataStore {
  private epdDataService: ManufacturerEpdDataService;

  // State properties
  epdDataOptions: { [source: string]: SelectOption[] } = {};
  epdRawData: ManufacturerEpdData[] = [];
  selectedEpdDataIds: string[] = [];
  defaultSource: string = '';
  isModalOpen: boolean = false;
  isLoading: boolean = false;
  error: string | null = null;
  sourceType: string = '';
  manufacturerId: string = '';
  productGroupId: string | null = null;
  sourceNames: string[] = ['Source 1', 'Source 2', 'Source 3'];
  translations: Partial<ProductEpdDataModalTranslations> = {};
  requestMissingEpdTranslations: Partial<RequestMissingEpdModalTranslations> = {};

  private updateState: (partial: Partial<ProductEpdDataStore>) => void;

  constructor(updateState: (partial: Partial<ProductEpdDataStore>) => void) {
    this.epdDataService = new ManufacturerEpdDataService();
    this.updateState = updateState;
  }

  // Simple setters
  setModalOpen = (open: boolean): void => {
    this.isModalOpen = open;
    this.updateState({ isModalOpen: open });
  };

  setSourceType = (sourceType: string): void => {
    this.sourceType = sourceType;
    this.updateState({ sourceType });
  };

  setManufacturerId = (manufacturerId: string): void => {
    this.manufacturerId = manufacturerId;
    this.updateState({ manufacturerId });
  };

  setProductGroupId = (productGroupId: string | null): void => {
    this.productGroupId = productGroupId;
    this.updateState({ productGroupId });
  };

  setSourceNames = (names: string[]): void => {
    this.sourceNames = names;
    this.updateState({ sourceNames: names });
  };

  setTranslations = (translations: Partial<ProductEpdDataModalTranslations>): void => {
    this.translations = translations;
    this.updateState({ translations });
  };

  setRequestMissingEpdTranslations = (translations: Partial<RequestMissingEpdModalTranslations>): void => {
    this.requestMissingEpdTranslations = translations;
    this.updateState({ requestMissingEpdTranslations: translations });
  };

  setSelectedEpdDataIds = (ids: string[]): void => {
    this.selectedEpdDataIds = ids;
    this.updateState({ selectedEpdDataIds: ids });
  };

  updateSelectedData = (selectedData: SelectedEpdData[], defaultSource: string): void => {
    // Convert to string array for internal state (just the IDs)
    const ids = selectedData.map(item => item.id);
    this.selectedEpdDataIds = ids;
    this.defaultSource = defaultSource;
    this.updateState({
      selectedEpdDataIds: ids,
      defaultSource,
    });
  };

  // Fetch EPD data for all sources
  fetchEpdData = async (): Promise<void> => {
    if (!this.manufacturerId) {
      this.error = 'Manufacturer ID is required';
      this.updateState({ error: 'Manufacturer ID is required' });
      console.error('[productEpdDataStore] Manufacturer ID is missing');
      return;
    }

    if (!this.sourceType) {
      this.error = 'Source type is required';
      this.updateState({ error: 'Source type is required' });
      console.error('[productEpdDataStore] Source type is missing');
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.updateState({ isLoading: true, error: null });

    try {
      const response: any = await this.epdDataService.list(this.manufacturerId, this.sourceType, this.productGroupId);
      
      // Handle different response formats
      let data: ManufacturerEpdData[];
      if (Array.isArray(response)) {
        data = response as ManufacturerEpdData[];
      } else if (response && response.result) {
        data = (response.result || []) as ManufacturerEpdData[];
      } else {
        data = [];
      }
      
      // Store raw data for EPD type processing
      this.epdRawData = data;
      
      // Group by epdInfoSource
      const groupedBySource: { [source: string]: ManufacturerEpdData[] } = {};
      
      data.forEach((item) => {
        const source = item.epdInfoSource || 'Unknown';
        if (!groupedBySource[source]) {
          groupedBySource[source] = [];
        }
        groupedBySource[source].push(item);
      });

      // Create options for each source, grouped by epdInfoName
      const optionsMap: { [source: string]: SelectOption[] } = {};

      Object.keys(groupedBySource).forEach((source) => {
        const items = groupedBySource[source];
        
        // Sort by epdInfoName
        items.sort((a, b) => (a.epdInfoName || '').localeCompare(b.epdInfoName || ''));
        
        const options: SelectOption[] = items.map((item) => ({
          value: item.epdDataId || '',
          label: item.epdDataLabel || '',
        }));

        optionsMap[source] = options;
      });

      this.epdDataOptions = optionsMap;
      this.isLoading = false;
      this.updateState({ epdDataOptions: optionsMap, epdRawData: data, isLoading: false });
    } catch (err) {
      console.error('Error fetching EPD data:', err);
      this.error = 'Failed to load Manufacturer EPD data. Please try again.';
      this.isLoading = false;
      this.updateState({
        error: 'Failed to load Manufacturer EPD data. Please try again.',
        isLoading: false,
      });
    }
  };

  reset = (): void => {
    this.epdDataOptions = {};
    this.epdRawData = [];
    this.selectedEpdDataIds = [];
    this.defaultSource = '';
    this.isModalOpen = false;
    this.isLoading = false;
    this.error = null;
    this.sourceType = '';
    this.manufacturerId = '';
    this.productGroupId = null;
    this.translations = {};
    this.requestMissingEpdTranslations = {};
    this.updateState({
      epdDataOptions: {},
      selectedEpdDataIds: [],
      defaultSource: '',
      isModalOpen: false,
      isLoading: false,
      error: null,
      sourceType: '',
      manufacturerId: '',
      productGroupId: null,
    });
  };
}

// Create the store instance immediately, not lazily
const storeInstance = new ProductEpdDataStore(() => {
  // This callback will be set when Zustand creates the store
});

export const useProductEpdDataStore = create<ProductEpdDataState>((set) => {
  // Update the store instance's updateState callback
  storeInstance['updateState'] = (partial) => set(partial as Partial<ProductEpdDataState>);
  return storeInstance as ProductEpdDataState;
});

// Export the store instance directly for use outside React components
export const getStoreInstance = (): ProductEpdDataStore => {
  return storeInstance;
};
